import React, { useMemo, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { colors } from 'app/shared/theme/colors';
import { useTranslation } from 'app/shared/i18n';

interface CodeItem {
  email?: string;
  emai?: string;
  emailpass?: string;
  code?: string;
  subject?: string;
  time?: string;
  message?: string;
}

const SOCIALS: { key: string; label: string; color: string }[] = [
  { key: 'facebook', label: 'Facebook', color: '#1877F2' },
  { key: 'instagram', label: 'Instagram', color: '#E4405F' },
  { key: 'tiktok', label: 'TikTok', color: '#69C9D0' },
  { key: 'twitter', label: 'X / Twitter', color: '#1DA1F2' },
  { key: 'apple', label: 'Apple', color: '#A2AAAD' },
  { key: 'amazon', label: 'Amazon', color: '#FF9900' },
  { key: 'shopee', label: 'Shopee', color: '#EE4D2D' },
  { key: 'lazada', label: 'Lazada', color: '#0F136D' },
  { key: 'kakao', label: 'Kakao', color: '#FEE500' },
  { key: 'telegram', label: 'Telegram', color: '#0088CC' },
];

const oneLine: React.CSSProperties = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const twoLine: React.CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  maxHeight: 34,
  lineHeight: '17px',
};

interface ChipProps {
  active: boolean;
  onClick: () => void;
  color?: string;
  children: React.ReactNode;
}
const Chip: React.FC<ChipProps> = ({ active, onClick, color, children }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      padding: '6px 12px',
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 500,
      border: `1px solid ${active ? color || colors.accent : colors.border}`,
      background: active ? (color ? color + '22' : colors.accentDim) : 'transparent',
      color: active ? color || colors.accent : colors.muted,
      cursor: 'pointer',
      fontFamily: 'inherit',
      transition: 'all 0.15s',
    }}
  >
    {children}
  </button>
);

export const GetCodeMail = () => {
  const { t } = useTranslation();
  const [inputListMail, setInputListMail] = useState('');
  const [loading, setLoading] = useState(false);
  const [dataTable, setDataTable] = useState<CodeItem[]>([]);
  const [totalItemCheck, setTotalItemCheck] = useState(0);
  const [itemChecked, setItemChecked] = useState(0);

  const progress = useMemo(
    () => (totalItemCheck === 0 ? 0 : +((itemChecked / totalItemCheck) * 100).toFixed(1)),
    [itemChecked, totalItemCheck],
  );

  const [socialCheckboxes, setSocialCheckboxes] = useState<Record<string, boolean>>(
    SOCIALS.reduce(
      (a, s) => {
        a[s.key] = false;
        return a;
      },
      {} as Record<string, boolean>,
    ),
  );
  const [typeCheckboxes, setTypeCheckboxes] = useState({ pop3: false, oauth2: false });

  const allSelected = SOCIALS.every(s => socialCheckboxes[s.key]);

  const handleAuto = () => {
    const next = !allSelected;
    setSocialCheckboxes(
      SOCIALS.reduce(
        (a, s) => {
          a[s.key] = next;
          return a;
        },
        {} as Record<string, boolean>,
      ),
    );
  };
  const handleSocial = (k: string) => setSocialCheckboxes(p => ({ ...p, [k]: !p[k] }));
  const handleType = (k: 'pop3' | 'oauth2') => setTypeCheckboxes(p => ({ ...p, [k]: !p[k] }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTotalItemCheck(0);
    setItemChecked(0);
    setDataTable([]);
    const dataValid = inputListMail.split('\n').filter(x => !!x.trim());
    setTotalItemCheck(dataValid.length);
    if (dataValid.length === 0) {
      Swal.fire({ title: t('toolGetCode.fillMailList'), icon: 'error' });
      return;
    }
    const selectedSocials = Object.keys(socialCheckboxes).filter(k => socialCheckboxes[k]);
    if (selectedSocials.length === 0) {
      Swal.fire({ title: t('toolGetCode.selectEmailType'), icon: 'error' });
      return;
    }
    const data = {
      type: Object.keys(typeCheckboxes)
        .filter(k => typeCheckboxes[k as 'pop3' | 'oauth2'])
        .join(),
      social: selectedSocials.join(),
      emailpass: dataValid,
    };
    try {
      setLoading(true);
      const { data: dataRes } = await axios.post('/api/tools/get-code-mail', data);
      const sessionId = dataRes.data;
      const eventSource = new EventSource(`/api/tools/get-code-mail/stream?sessionId=${sessionId}`);
      eventSource.onmessage = event => {
        const d = JSON.parse(event.data);
        setDataTable(p => [...p, d]);
        setItemChecked(p => p + 1);
      };
      eventSource.onerror = () => {
        eventSource.close();
        setLoading(false);
        Swal.fire({ title: t('toolCommon.complete'), icon: 'success' });
      };
    } catch {
      setLoading(false);
      Swal.fire({ title: t('toolCommon.errorOccurred'), icon: 'error' });
    }
  };

  const retry = async (emailpass: string) => {
    const selectedSocials = Object.keys(socialCheckboxes).filter(k => socialCheckboxes[k]);
    if (selectedSocials.length === 0) {
      Swal.fire({ title: t('toolGetCode.selectEmailType'), icon: 'error' });
      return;
    }
    const data = {
      type: Object.keys(typeCheckboxes)
        .filter(k => typeCheckboxes[k as 'pop3' | 'oauth2'])
        .join(),
      social: selectedSocials.join(),
      emailpass: [emailpass],
    };
    try {
      setTotalItemCheck(1);
      setItemChecked(0);
      setLoading(true);
      const { data: dataRes } = await axios.post('/api/tools/get-code-mail', data);
      const sessionId = dataRes.data;
      const eventSource = new EventSource(`/api/tools/get-code-mail/stream?sessionId=${sessionId}`);
      eventSource.onmessage = event => {
        const d = JSON.parse(event.data);
        setDataTable(p => p.map(o => (o.emailpass === d.emailpass ? d : o)));
        setItemChecked(p => p + 1);
      };
      eventSource.onerror = () => {
        eventSource.close();
        setLoading(false);
        Swal.fire({ title: t('toolCommon.complete'), icon: 'success' });
      };
    } catch {
      setLoading(false);
      Swal.fire({ title: t('toolCommon.errorOccurred'), icon: 'error' });
    }
  };

  const onCopy = (data: string) => {
    navigator.clipboard.writeText(data);
    toast.success(t('toolCommon.copySuccess'));
  };

  const okCount = dataTable.filter(x => x.code).length;
  const failCount = dataTable.length - okCount;

  return (
    <div style={{ maxWidth: 1040, margin: '0 auto', padding: '36px 24px' }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 10 }}>🔑 Get Code Email</h2>
      <p style={{ color: colors.muted, fontSize: 13, margin: '0 0 20px' }}>
        {t('toolGetCode.subtitle', { defaultValue: 'Lấy code xác thực từ các hộp thư' })}
      </p>

      <div className="ms-card" style={{ padding: 20, marginBottom: 20 }}>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14 }}>
            <div
              style={{ fontSize: 11, fontWeight: 600, color: colors.dim, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}
            >
              {t('toolGetCode.authType', { defaultValue: 'Auth type' })}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Chip active={typeCheckboxes.pop3} onClick={() => handleType('pop3')}>
                POP3
              </Chip>
              <Chip active={typeCheckboxes.oauth2} onClick={() => handleType('oauth2')}>
                OAuth2
              </Chip>
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <div
              style={{ fontSize: 11, fontWeight: 600, color: colors.dim, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}
            >
              {t('toolGetCode.provider', { defaultValue: 'Provider' })}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Chip active={allSelected} onClick={handleAuto}>
                ✨ Auto (all)
              </Chip>
              {SOCIALS.map(s => (
                <Chip key={s.key} active={socialCheckboxes[s.key]} color={s.color} onClick={() => handleSocial(s.key)}>
                  {s.label}
                </Chip>
              ))}
            </div>
          </div>

          <textarea
            className="ms-textarea"
            style={{ height: 160, fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}
            value={inputListMail}
            placeholder="mail|pass|refresh_token|client_id"
            onChange={e => setInputListMail(e.target.value)}
          />
          <button className="ms-btn ms-btn--primary" type="submit" disabled={loading} style={{ width: '100%', marginTop: 12 }}>
            {loading ? `⏳ ${t('toolCommon.processing')}` : '▶ Get Code'}
          </button>
        </form>

        {(loading || progress > 0) && (
          <div style={{ marginTop: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 12, color: colors.dim }}>
                {t('toolCommon.progress')} · {itemChecked}/{totalItemCheck}
              </span>
              <span style={{ fontSize: 12, fontWeight: 600, color: colors.accent, fontFamily: "'JetBrains Mono', monospace" }}>
                {progress}%
              </span>
            </div>
            <div className="ms-progress">
              <div className="ms-progress__fill" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}
      </div>

      {dataTable.length > 0 && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <span className="ms-badge" style={{ background: colors.surface, color: colors.muted }}>
            📬 {dataTable.length} total
          </span>
          <span className="ms-badge ms-badge--success">✓ {okCount} ok</span>
          {failCount > 0 && <span className="ms-badge ms-badge--danger">✗ {failCount} failed</span>}
        </div>
      )}

      {dataTable.map((item, i) => {
        const email = item.email || item.emai || item.emailpass?.split('|')[0] || '';
        const ok = !!item.code;
        return (
          <div
            key={i}
            className="ms-card"
            style={{
              padding: 12,
              marginBottom: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              borderLeft: `3px solid ${ok ? colors.success : colors.danger}`,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: ok ? colors.successDim : colors.dangerDim,
                color: ok ? colors.success : colors.danger,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                flexShrink: 0,
              }}
            >
              {ok ? '✓' : '✗'}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: colors.text, ...oneLine }}>{email}</div>
              <div style={{ fontSize: 12, color: colors.muted, ...twoLine }}>
                {ok ? item.subject || '-' : item.message || t('toolGetCode.noCode', { defaultValue: 'Không tìm thấy code' })}
              </div>
            </div>

            {ok && item.code && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 12px',
                  background: colors.accentDim,
                  border: `1px dashed ${colors.accent}`,
                  borderRadius: 6,
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 15,
                    fontWeight: 700,
                    color: colors.accent,
                    letterSpacing: 1,
                  }}
                >
                  {item.code}
                </span>
                <button className="ms-btn ms-btn--ghost" onClick={() => onCopy(item.code)} style={{ padding: '2px 8px', fontSize: 11 }}>
                  📋
                </button>
              </div>
            )}

            {item.time && (
              <span style={{ fontSize: 11, color: colors.dim, flexShrink: 0, fontFamily: "'JetBrains Mono', monospace" }}>{item.time}</span>
            )}

            {item.emailpass && (
              <button
                className="ms-btn ms-btn--ghost"
                onClick={() => retry(item.emailpass)}
                disabled={loading}
                style={{ padding: '4px 10px', fontSize: 11, flexShrink: 0 }}
              >
                ↻
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};
